import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import CountryCodes from '../../../CountryCodes.json';

// Convert ISO country code to flag emoji (e.g., "US" → "🇺🇸")
const getFlagEmoji = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 0x1F1E6 + char.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
};

interface CountryCodeSelectorProps {
  /** Selected value - can be ISO code (IN) or dial code (+91) based on useIsoCode prop */
  value: string;
  /** Callback when value changes */
  onValueChange: (value: string) => void;
  /** If true, uses ISO code (IN) as value; if false, uses dial_code (+91) */
  useIsoCode?: boolean;
  /** Default value if none provided (defaults to India +91) */
  defaultCode?: string;
  /** Additional class names for the component wrapper */
  className?: string;
  /** Additional class names for the trigger button */
  triggerClassName?: string;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

export function CountryCodeSelector({
  value,
  onValueChange,
  useIsoCode = false,
  defaultCode,
  className,
  triggerClassName,
  placeholder = 'Select country',
  disabled = false,
}: CountryCodeSelectorProps) {
  const [open, setOpen] = useState(false);

  // Find the currently selected country
  const selectedCountry = useMemo(() => {
    if (!value) return null;
    
    if (useIsoCode) {
      return CountryCodes.find(c => c.code === value);
    } else {
      return CountryCodes.find(c => c.dial_code === value);
    }
  }, [value, useIsoCode]);

  // Default to India if no value and defaultCode not specified
  const effectiveDefault = defaultCode || (useIsoCode ? 'IN' : '+91');
  
  // Get display content for the trigger button
  const displayContent = useMemo(() => {
    const country = selectedCountry || CountryCodes.find(c => 
      useIsoCode ? c.code === effectiveDefault : c.dial_code === effectiveDefault
    );
    if (country) {
      return {
        flag: getFlagEmoji(country.code),
        text: `${country.dial_code} ${country.name}`
      };
    }
    return { flag: '', text: placeholder };
  }, [selectedCountry, effectiveDefault, useIsoCode, placeholder]);

  const handleSelect = (selectedValue: string) => {
    const country = CountryCodes.find(c => c.code === selectedValue);
    if (country) {
      onValueChange(useIsoCode ? country.code : country.dial_code);
    }
    setOpen(false);
  };

  // Get the current selection's ISO code for comparison
  const currentIsoCode = useMemo(() => {
    if (!value) return effectiveDefault;
    if (useIsoCode) return value;
    const country = CountryCodes.find(c => c.dial_code === value);
    return country?.code || '';
  }, [value, useIsoCode, effectiveDefault]);

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select country code"
            disabled={disabled}
            className={cn(
              'justify-between font-normal',
              triggerClassName
            )}
          >
            <span className="truncate flex items-center gap-1.5">
              <span style={{ fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Twemoji Mozilla", sans-serif' }}>
                {displayContent.flag}
              </span>
              {displayContent.text}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0 bg-background border border-border shadow-lg z-50" align="start">
          <Command>
            <CommandInput placeholder="Search country or code..." className="h-9" />
            <CommandList className="max-h-[300px]">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {CountryCodes.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={`${country.name} ${country.dial_code}`}
                    onSelect={() => handleSelect(country.code)}
                  >
                    <span className="flex-1 truncate flex items-center gap-1.5">
                      <span style={{ fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Twemoji Mozilla", sans-serif' }}>
                        {getFlagEmoji(country.code)}
                      </span>
                      {country.dial_code} {country.name}
                    </span>
                    <Check
                      className={cn(
                        'ml-2 h-4 w-4 shrink-0',
                        currentIsoCode === country.code ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
